import { createElement, empty, hide, show } from "../../utils/vanilla";
import { CancelablePromise as Promise } from "../../utils/cancelablePromise";
import Spinner from "../../utils/spinner";
import { dispatcher, DispatcherEvents } from "../../utils/dispatcher";

export interface IBarButton {
    title: string;
    id: string;
    clickHandler?: (event: MouseEvent) => void | Promise;
    clickWrapper?: (event: MouseEvent) => void | Promise;
    className?: string;
    icon?: string;
    elem?: HTMLButtonElement | null;
}

export interface IBarOptions {
    isNightMode: boolean;
    textArea?: ITextAreaOptions;
    showAnimation?: boolean;
}

interface ITextAreaOptions {
    id: string;
    placeHolderText?: string;
    defaultMessage?: string;
    contentEditable?: boolean;
}

export default class ButtonsBar {
    private readonly wrapper: HTMLDivElement;
    private readonly enableButtonElRef = this.enableButtonIfNotEmpty.bind(this);
    private textArea: HTMLInputElement;
    private buttons: IBarButton[] = [];

    constructor (buttons: IBarButton[], private readonly options: IBarOptions) {
        this.buttons = buttons;

        let wrapper = createElement("div", `panel buttons-bar${ options.isNightMode ? " nightMode" : "" }`, "buttons-bar-id");
        this.wrapper = wrapper;
        this.construct();
    }

    get waiting () {
        return {
            start: () => {
                this.wrapper.classList.add("hidden");
                let spinnerWrapper = createElement("div", "buttons-bar__waiting");
                this.wrapper.parentNode?.insertBefore(spinnerWrapper, this.wrapper.parentNode.firstChild);
                new Spinner({ top: 5 }).spin(spinnerWrapper);
            },
            stop: () => {
                let spinnerWrapper = this.wrapper.parentNode?.querySelector(".buttons-bar__waiting");
                spinnerWrapper && this.wrapper.parentNode?.removeChild(spinnerWrapper);
                this.wrapper.classList.remove("hidden");
            }
        };
    }

    disableButtons () {
        let buttons = this.buttons.map(button => button.id);
        buttons.forEach(buttonId => {
            let button = this.wrapper.querySelector(`#${ buttonId }`);
            button && button.setAttribute("disabled", "disabled");
        });
    }

    enableButtons () {
        let buttons = this.buttons.map(button => button.id);
        buttons.forEach(buttonId => {
            let button = this.wrapper.querySelector(`#${ buttonId }`);
            button && button.removeAttribute("disabled");
        });
    }

    show () {
        if (!this.isVisible()) {
            let domContainer = document.getElementById("buttons-bar-wrapper");
            if (domContainer) {
                empty(domContainer);
                show(domContainer);
                domContainer.appendChild(this.wrapper);
                domContainer.style.height = this.options.showAnimation ? `${ this.wrapper.getBoundingClientRect().height }px` : "auto";
                this.textArea && this.enableButtonIfNotEmpty();
            }
        }
    }

    hide () {
        if (this.isVisible()) {
            this.waiting.stop();

            let parent = this.wrapper.parentElement;
            parent && parent.removeChild(this.wrapper);

            let domContainer = document.getElementById("buttons-bar-wrapper");
            if (domContainer) {
                hide(domContainer);
                domContainer.style.height = "0px";
            }
        }

        this.textArea && this.textArea.removeEventListener("input", this.enableButtonElRef, false);

        this.buttons.forEach(button => {
            if (button.elem && button.clickWrapper) {
                button.elem.removeEventListener("click", button.clickWrapper);
                delete button.clickWrapper;
                delete button.elem;
            }
        });
        this.buttons = [];
    }

    private enableButtonIfNotEmpty(e?: Event) {
        let textArea = (e?.currentTarget || this.textArea) as HTMLInputElement;
        let buttons = document.getElementsByClassName("buttons-bar__button");
        if (buttons?.length > 0) {
            if (textArea && !(textArea.innerText?.trim())) {
                Array.prototype.forEach.call(buttons, button => button.disabled = true);
            } else {
                Array.prototype.forEach.call(buttons, button => button.disabled = false);
            }
        }
    }

    private construct() {
        // Text area is made to support one button for now. Will have to modify css for other uses.
        if (this.options.textArea) {
            let textAreaRegion = createElement("div", "textarea__content");
            let textArea = createElement("span", "textarea", this.options.textArea.id);
            textArea.setAttribute("role", "textbox");
            textArea.setAttribute("aria-label", `${ this.options.textArea.placeHolderText ? this.options.textArea.placeHolderText : "Message Input" }`);
            this.options.textArea.placeHolderText && textArea.setAttribute("data-placeholder", this.options.textArea.placeHolderText);
            this.options.textArea.defaultMessage && (textArea.innerText = this.options.textArea.defaultMessage);
            textArea.contentEditable = (this.options.textArea.contentEditable || this.options.textArea.contentEditable === undefined) ? "true" : "false";

            this.textArea = textArea as HTMLInputElement;
            this.textArea.addEventListener("input", this.enableButtonElRef, false);

            textAreaRegion.appendChild(this.textArea);
            this.wrapper.appendChild(textAreaRegion);
            this.wrapper.style.display = "flex";
        }

        if (this.buttons.length) {
            let buttonsRegion = createElement("div", "buttons-bar__content");
            let buttonsRow;
            this.buttons.forEach((button, i) => {
                if (i % 2 === 0) {
                    buttonsRow && buttonsRegion.appendChild(buttonsRow);
                    buttonsRow = createElement("div", "buttons-bar__row");
                }

                if (!button.clickHandler) {
                    return;
                }

                let btnElem = createElement("button", `${ button.className || "" } buttons-bar__button`, button.id);

                button.clickWrapper = e => {
                    dispatcher.trigger(DispatcherEvents.DriveAppInteraction, e);
                    button.clickHandler && button.clickHandler(e);
                };

                btnElem.addEventListener("click", button.clickWrapper, false);
                btnElem.innerHTML = `${ button.icon ? `<svg class="svgIcon big" role="img" aria-label="Toolbar icon"><use xlink:href="#${ button.icon }"></use></svg> ` : "" }${ button.title }`;
                button.elem = btnElem;
                buttonsRow.appendChild(btnElem);

                if (i === this.buttons.length - 1) {
                    buttonsRow && buttonsRegion.appendChild(buttonsRow);
                }
            });

            this.wrapper.appendChild(buttonsRegion);
        }
    }

    private isVisible () {
        return document.getElementById(this.wrapper.id);
    }
}